import { getMarketingConsent, hasMarketingConsent } from '@/utils/consent'

const ONESIGNAL_APP_ID = '6f837cfe-09f1-11e5-910c-4b447621c058'

type OneSignalSDK = typeof import('react-onesignal').default
export type OneSignalUserInput = { id: string | number | null | undefined }

let sdk: OneSignalSDK | undefined
let started = false
let initialized = false
let initialization: Promise<void> | undefined
// undefined means auth has not reported yet; null explicitly detaches the user.
let desired: string | null | undefined
// The first auth report restores state; only a later change requires detachment.
let identityChanged = false
let queue: Promise<void> = Promise.resolve()
let pushCleanup: Promise<void> | undefined

const reportError = (error: unknown): void => {
  console.error('OneSignal sync failed:', error)
}

// SDK logout is itself consent-gated. Disable the browser endpoint instead when
// auth changes without consent, without loading the SDK or enabling collection.
const stopExistingPush = (): Promise<void> => {
  if (pushCleanup) return pushCleanup
  pushCleanup = (async () => {
    if (typeof navigator === 'undefined' || !navigator.serviceWorker) return
    const registrations = await navigator.serviceWorker.getRegistrations()
    await Promise.all(registrations.map(async (registration) => {
      const worker = registration.active || registration.waiting || registration.installing
      if (!worker || new URL(worker.scriptURL).pathname !== '/push/onesignal/worker/OneSignalSDKWorker.js') return
      const subscription = await registration.pushManager.getSubscription()
      if (subscription && identityChanged && getMarketingConsent() === false && !await subscription.unsubscribe()) {
        throw new Error('OneSignal push subscription could not be disabled')
      }
    }))
  })().catch(reportError).finally(() => { pushCleanup = undefined })
  return pushCleanup
}

const stopStalePush = async (): Promise<void> => {
  if (identityChanged && getMarketingConsent() === false) await stopExistingPush()
}

const applyDesiredIdentity = async (): Promise<void> => {
  // Finish endpoint removal before a re-grant can initialize or link a user.
  await pushCleanup
  if (!hasMarketingConsent()) return
  // The wrapper injects the CDN script at import time. All callers go through
  // this owner so a declined or unknown consent never loads it eagerly.
  if (!sdk) sdk = (await import('react-onesignal')).default
  if (!hasMarketingConsent()) return

  if (!initialization) {
    await sdk.setConsentRequired(true)
    await sdk.setConsentGiven(false)
    if (!hasMarketingConsent()) return
    // Start disabled even if an earlier page persisted OneSignal consent.
    // Do not retry a partially failed SDK init in the same page load.
    initialization = sdk.init({ appId: ONESIGNAL_APP_ID, requiresUserPrivacyConsent: true })
  }
  await initialization
  initialized = true
  await sdk.setConsentGiven(hasMarketingConsent())
  if (!hasMarketingConsent()) {
    await stopStalePush()
    return
  }

  // Read the latest identity after initialization/consent have settled.
  const target = desired
  if (target === undefined) return
  if (target !== null) {
    if (sdk.User.externalId !== target) await sdk.login(target)
  } else if (sdk.User.externalId) {
    await sdk.logout()
  }
  // A pending identity write may settle after a sign-out or account switch.
  if (hasMarketingConsent() && target === desired) identityChanged = false
  await stopStalePush()
}

const flush = (): Promise<void> => {
  queue = queue.then(applyDesiredIdentity).catch(reportError)
  return queue
}

const updateConsent = (): void => {
  if (!hasMarketingConsent()) {
    // Revocation must not wait behind an in-flight login or SDK initialization.
    if (sdk) void sdk.setConsentGiven(false).catch(reportError)
    void stopStalePush()
    return
  }
  void flush()
}

/** Observe initial, restored and changed Cookiebot consent once per page. */
export const startOneSignal = (): void => {
  if (!started) {
    started = true
    window.addEventListener('CookiebotOnConsentReady', updateConsent)
    window.addEventListener('CookiebotOnAccept', updateConsent)
    window.addEventListener('CookiebotOnDecline', updateConsent)
  }
  updateConsent()
}

/** Link only the internal user id; email subscriptions are not managed here. */
export const syncOneSignalUser = (user: OneSignalUserInput | null): void => {
  const previous = desired
  if (user === null) {
    desired = null
  } else {
    if (user.id === null || user.id === undefined || String(user.id).trim() === '') return
    desired = String(user.id)
  }
  // Do not wait behind startup or an in-flight SDK login to stop delivery.
  if (previous !== undefined && previous !== desired) identityChanged = true
  void stopStalePush()
  if (started) void flush()
}

/** Keep tutorial push prompts behind the same consent and readiness gates. */
export const promptOneSignalPush = (): void => {
  if (!started || !hasMarketingConsent()) return
  void flush().then(async () => {
    if (initialized && hasMarketingConsent()) await sdk?.Slidedown.promptPush()
  }).catch(reportError)
}
